import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import axios from 'axios';
import produce from 'immer';
import { PaginationType } from 'types/response.d';
import { PageRequest } from 'types/common.d';
import { UserType } from 'types/entities/users.d';
import { UsersModel } from 'types/models/users.d';
import { DEFAULT_PAGINATION } from 'commons/constants';
import { storeName as entityStore } from 'store/entities/roles.entity';
import { RootModel } from '../models';

export const storeName = 'rolesModel';

const initialState: UsersModel = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: {
    keyword: '',
  },
  permissions: {
    content: [],
    pagination: DEFAULT_PAGINATION,
  },
};

export const rolesModel = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setPermissions: produce((state, payload) => {
      state.permissions = { ...payload };
    }),
  },
  effects: (dispatch: RematchDispatch<typeof rolesModel>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria });
    },
    async filterAsync(payload) {
      this.updateLoading(true);
      try {
        const data: { content: UserType[] } & PaginationType = await dispatch[entityStore].load(
          payload
        );
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.filterAsync({ ...pagination, ...criteria, ...payload });
    },
    async getAsync(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async publishAsync(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch[entityStore].publish(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const data = payload;
        const uploadActions: Promise<void>[] = [];
        await axios.all(uploadActions);
        await dispatch[entityStore].save(data);
        message.success('Save successfully');
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }
    },
    async loadPermissions(params, state) {
      const data = await dispatch[entityStore].loadPermissions(params);
      if (data && data.content) {
        const { content, ...pagination } = data;
        this.setPermissions({
          content,
          pagination,
        });
      }
    },
  }),
});

export default rolesModel;
